import StarBorderIcon from '@mui/icons-material/StarBorder';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import BlockedGetAccess from 'molecules/BlockedGetAccess';
import ImageGallery from 'molecules/ImageGallery';
import InvestmentHolding from 'molecules/InvestmentInfo/InvestmentHolding';
import InvestmentStats from 'molecules/InvestmentInfo/InvestmentStats';
import Watch from 'molecules/Watch';
import React, { useEffect, useState } from 'react';
import { OfferingType } from 'stores/networkInterfaces';
import { canWatchOffering, getOfferingTag, OfferingItem } from 'stores/offeringUtils';
import { useStore } from 'stores/store';
import { IInvestment, IOfferingInfo } from 'utils/globalTypes';
import { useBlockedAction } from 'utils/hooks/useBlockedAction';
import { useUserAuth } from 'utils/hooks/useUserAuth';
import { getGalleryItems, nullToEmptyStr } from 'utils/object';

import s from './BrowsingItem.module.scss';
import BrowsingItemActions from './BrowsingItemActions';
import BrowsingItemFooter from './BrowsingItemFooter';

interface BrowsingItemProps {
  item: IOfferingInfo;
  isPOI?: boolean;
  showStats?: boolean;
  showActions?: boolean;
  className?: string;
  onNavigate?: (item?: IInvestment) => void;
  getFields?: (item: IInvestment) => OfferingItem[];
}

const BrowsingItem = ({
  item,
  isPOI,
  showStats = true,
  showActions = true,
  className,
  onNavigate,
  getFields
}: BrowsingItemProps) => {
  const { AssetStore } = useStore();
  const authDetails = useUserAuth();
  const { onBlockedAction, blockedPrimaryTitle, requestAccessModal } = useBlockedAction(item);

  const [isHover, setHover] = useState<boolean>();

  const { offering_id, media } = item;

  const isActive = AssetStore.activeItemId == offering_id;
  const isSelected = AssetStore.selectedItemId == offering_id;

  const galleryMedia = getGalleryItems(media, { width: 560, quality: 75 });

  const showWatch =
    showActions && canWatchOffering(item, authDetails?.isApproved, authDetails?.isManagement);

  useEffect(() => {
    // close POI on unmount
    return () => {
      AssetStore.clearSelected(item);
    };
  }, []);

  const setToggle = (activeState: boolean) => {
    setHover(activeState);
    AssetStore.setActiveItem(item, activeState);
  };

  const withActions = showActions && authDetails?.isApproved && !authDetails?.isManagement;
  const isGuestMode = !item.offering_name;

  const imageGallery = (
    <ImageGallery
      className={s.BrowsingItem__gallery}
      images={galleryMedia}
      tag={showStats ? getOfferingTag(item) : undefined}
      showBullets={galleryMedia?.length > 1}
      isPreviewMode
      onClick={() => onNavigate && onNavigate(item)}
      image={{
        width: 318,
        height: 249
      }}
    />
  );

  return (
    <div
      className={cn(
        s.BrowsingItem,
        {
          isActive: (item && isActive) || isSelected,
          isSecondary: item?.offering_type === OfferingType.secondary,
          isPrimary: item?.offering_type === OfferingType.primary,
          isPromotion: item?.offering_type === OfferingType.promotion,
          isPOI
        },
        nullToEmptyStr(className)
      )}
      onMouseEnter={() => setToggle(true)}
      onMouseLeave={() => setToggle(false)}
    >
      <div className={s.BrowsingItem__wrapper}>
        {!authDetails?.isApproved || !item?.offering_name ? (
          <BlockedGetAccess
            primaryTitle={blockedPrimaryTitle}
            isHover={isHover}
            onAction={onBlockedAction}
          >
            {imageGallery}
          </BlockedGetAccess>
        ) : (
          imageGallery
        )}
        {!authDetails?.isManagement && showStats && (
          <InvestmentHolding
            item={item}
            className={s.BrowsingItem__holding}
            onClick={() => onNavigate && onNavigate(item)}
          />
        )}
        <div className={s.BrowsingItem__contentLink} onClick={() => onNavigate && onNavigate(item)}>
          <div className={s.BrowsingItem__content}>
            <div className={s.BrowsingItem__contentMain}>
              {showWatch && (
                <Watch
                  customClass={s.BrowsingItem__watch}
                  watch={item.watch}
                  offering_id={item.offering_id}
                  isAuthenticated={authDetails?.isAuthenticated}
                  isApproved={authDetails?.isApproved}
                  inactiveIcon={<StarBorderIcon className={s.BrowsingItem__watchInactive} />}
                />
              )}
              <Typography mb={1} variant="body2" color="text.secondary">
                {item.sponsor_name}
              </Typography>
              <div className={s.BrowsingItem__header}>
                <div className={s.BrowsingItem__headerMain}>
                  {item.offering_name && (
                    <Typography
                      sx={{ fontWeight: 'medium' }}
                      variant="h5"
                      className={s.BrowsingItem__title}
                    >
                      <Box component="span" color="primary.main">
                        {item.offering_id}
                      </Box>{' '}
                      <span className={cn(s.BrowsingItem__label)}>{item.offering_name}</span>
                    </Typography>
                  )}
                  {(item.asset_type || item.address) && (
                    <Typography
                      className={cn(s.BrowsingItem__description)}
                      sx={{ fontWeight: 'medium' }}
                      mt={0.75}
                      variant="body2"
                      color="text.secondary"
                    >
                      {item?.address?.short_formatted_address
                        ? [item.asset_type, item?.address?.short_formatted_address]
                            .filter(Boolean)
                            .join(', ')
                        : [item.asset_type, item?.address?.city, item?.address?.state_code]
                            .filter(Boolean)
                            .join(', ')}
                    </Typography>
                  )}
                </div>
                {item.investment_highlights && (
                  <div className={s.BrowsingItem__highlights}>
                    <Typography variant="body2" className={s.BrowsingItem__highlightsText}>
                      {item.investment_highlights}
                    </Typography>
                  </div>
                )}
                {!authDetails?.isManagement && showStats && (
                  <InvestmentStats item={item} className={s.BrowsingItem__stats} />
                )}
              </div>
            </div>
          </div>
          <div
            className={cn(s.BrowsingItem__info, {
              withActions,
              hideBorder: !showStats
            })}
          >
            {showStats && (
              <BrowsingItemFooter item={item} getFields={getFields} isGuestMode={isGuestMode} />
            )}
            {withActions && <BrowsingItemActions item={item} />}
          </div>
        </div>
      </div>
      {requestAccessModal}
    </div>
  );
};

export default observer(BrowsingItem);
